import * as React from 'react';
import type { JSX } from 'react';
import { enUS, ru } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { isValidTimeString } from './validation';

/** Default start time when a date is picked while the time field is still empty. */
const DEFAULT_TIME = '10:00';

/** Number of quick-slot chips shown after `now`. */
const QUICK_SLOT_COUNT = 4;

/** Quick slots are aligned to 30-minute boundaries. */
const QUICK_SLOT_MINUTES = 30;

/** Value the component passes for the time part when the current time is unusable. */
const EMPTY_TIME = '';

/** yyyy-mm-dd (the native date input's value) parsed as a local Date; null when not a real day. */
export function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) {
    return null;
  }
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, monthIndex, day);
  const valid =
    date.getFullYear() === year && date.getMonth() === monthIndex && date.getDate() === day;
  return valid ? date : null;
}

const pad2 = (value: number): string => `${value}`.padStart(2, '0');

/** yyyy-mm-dd of a local Date (the reverse of parseIsoDate). */
export function formatIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/**
 * The next `count` half-hour slots strictly after `now`, as HH:MM strings (e.g. now 14:07 ->
 * 14:30, 15:00, 15:30, 16:00). Pure and deterministic: tests inject `now`; a now exactly on a
 * slot boundary yields the following slots.
 */
export function nextHalfHourSlots(now: Date, count: number = QUICK_SLOT_COUNT): string[] {
  const slot = new Date(now.getTime());
  slot.setSeconds(0, 0);
  const offset = slot.getMinutes() % QUICK_SLOT_MINUTES;
  slot.setMinutes(slot.getMinutes() - offset + QUICK_SLOT_MINUTES);
  const slots: string[] = [];
  for (let index = 0; index < count; index += 1) {
    slots.push(`${pad2(slot.getHours())}:${pad2(slot.getMinutes())}`);
    slot.setMinutes(slot.getMinutes() + QUICK_SLOT_MINUTES);
  }
  return slots;
}

/** The date and time parts the field emits; date is yyyy-mm-dd, time is HH:MM or ''. */
export interface DateTimeFieldValue {
  readonly date: string;
  readonly time: string;
}

export interface DateTimeFieldProps {
  /** yyyy-mm-dd, '' while untouched (the trigger then shows a placeholder). */
  readonly date: string;
  /** HH:MM, '' while unset; an invalid draft value is emitted back as ''. */
  readonly time: string;
  /** All-day mode: the time input and the quick-slot chips are hidden. */
  readonly allDay: boolean;
  readonly onChange: (next: DateTimeFieldValue) => void;
  /** UI language: formats the trigger label and the calendar's weekday/month names. */
  readonly locale: 'en' | 'ru';
  /** yyyy-mm-dd; earlier days are disabled in the calendar. */
  readonly minDate?: string;
  /** Id for the date trigger; the time input gets `${id}-time`. */
  readonly id?: string;
  readonly disabled?: boolean;
  /** Test seam: the clock the quick slots and the Today button read; defaults to real time. */
  readonly now?: () => Date;
  readonly className?: string;
}

/**
 * Date/time picker control for the event forms: a popover calendar for the date plus a native
 * time input and quick-slot chips for the time, with an all-day mode that hides the time parts.
 * Mirrors the existing event-form validation contract: the time is emitted only as a valid
 * HH:MM (or '' so the form's own validation message shows), dates as yyyy-mm-dd; picking a date
 * keeps the current time or defaults to 10:00 and fires onChange once.
 */
export function DateTimeField({
  date,
  time,
  allDay,
  onChange,
  locale,
  minDate = '',
  id,
  disabled = false,
  now = (): Date => new Date(),
  className,
}: DateTimeFieldProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const autoId = React.useId();
  const fieldId = id ?? autoId;

  const selectedDay = React.useMemo(() => parseIsoDate(date), [date]);
  const dateFormatter = React.useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }),
    [locale],
  );
  const dayPickerLocale = locale === 'ru' ? ru : enUS;
  const slots = React.useMemo(() => nextHalfHourSlots(now()), [now]);
  const disabledDays = React.useMemo(() => {
    const minDay = parseIsoDate(minDate);
    return minDay === null ? undefined : [{ before: minDay }];
  }, [minDate]);

  const handleSelect = (day: Date | undefined): void => {
    if (day === undefined) {
      return;
    }
    setOpen(false);
    onChange({
      date: formatIsoDate(day),
      time: isValidTimeString(time) ? time : DEFAULT_TIME,
    });
  };

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    // The validation contract: emit only a complete, in-range HH:MM; anything else goes back
    // as '' so the form's own validation message takes over.
    onChange({ date, time: isValidTimeString(value) ? value : EMPTY_TIME });
  };

  return (
    <div data-slot="datetime-field" className={cn('flex flex-wrap items-center gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            id={fieldId}
            variant="outline"
            disabled={disabled}
            className={cn(
              'w-[11rem] justify-start px-3 text-left font-normal',
              selectedDay === null && 'text-muted-foreground',
            )}
          >
            <CalendarIcon className="opacity-50" />
            {selectedDay === null
              ? t('calendar.event.picker.open')
              : dateFormatter.format(selectedDay)}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="single"
            {...(selectedDay === null ? {} : { selected: selectedDay, defaultMonth: selectedDay })}
            onSelect={handleSelect}
            {...(disabledDays === undefined ? {} : { disabled: disabledDays })}
            locale={dayPickerLocale}
            weekStartsOn={locale === 'ru' ? 1 : 0}
          />
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              disabled={disabled}
              onClick={(): void => handleSelect(now())}
            >
              {t('calendar.event.picker.today')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {!allDay && (
        <>
          <Input
            id={id === undefined ? undefined : `${id}-time`}
            type="time"
            value={time}
            aria-label={t('calendar.event.picker.time')}
            disabled={disabled}
            onChange={handleTimeChange}
            className="w-28"
          />
          <div
            role="group"
            aria-label={t('calendar.event.picker.slots')}
            className="flex flex-wrap items-center gap-1"
          >
            {slots.map((slot) => (
              <Button
                key={slot}
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={(): void => onChange({ date, time: slot })}
              >
                {slot}
              </Button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
